/**
 * Super Admin — grant, change and revoke a user's workspace access in one pass.
 *
 * Nothing is written until Save: every edit lands in a local draft, so granting
 * ten workspaces is one action instead of ten round-trips.
 *
 * The checkbox is a SELECTION tool, not the access state — access is the role
 * dropdown ("" = none). Conflating the two would leave bulk apply able to touch
 * only existing members, which is the opposite of the point.
 *
 * @module components/admin/ManageAccessModal
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import {
  setWorkspaceRole,
  removeMember,
  type AdminUser,
  type AdminWorkspace,
} from "../../services/adminService";
import type { WorkspaceRole } from "../../types";

const ROLES: WorkspaceRole[] = ["admin", "analyst", "viewer"];

/** workspaceId -> role, or null for no access. */
type Draft = Record<string, WorkspaceRole | null>;

export interface ManageAccessModalProps {
  isOpen: boolean;
  user: AdminUser | null;
  workspaces: AdminWorkspace[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const buildDraft = (user: AdminUser | null, workspaces: AdminWorkspace[]): Draft => {
  const next: Draft = {};
  workspaces.forEach((w) => {
    next[w.id] = user?.workspaces.find((uw) => uw.id === w.id)?.role ?? null;
  });
  return next;
};

export const ManageAccessModal: React.FC<ManageAccessModalProps> = ({
  isOpen,
  user,
  workspaces,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Draft>(() => buildDraft(user, workspaces));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRole, setBulkRole] = useState<string>("");
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Retry contract: unlike the reseed effect below, this recomputes on every
  // prop REFERENCE change, on purpose — it is what makes a fail-then-retry Save
  // cycle correct. On partial failure `draft` is left exactly as the user last
  // edited it and the modal stays open (see `handleSave`), so the only way a row
  // that DID succeed can drop out of `pending` is for `original` to catch up to
  // it. That requires the caller to react to `onSaved()` by re-passing `user` as
  // a NEW object reflecting the just-attempted writes — roles are read from
  // `user.workspaces`, while the `workspaces` prop only supplies the key set, so
  // a fresh `user` is what resolves a retry. The real caller does this via
  // `load(true)` plus the `selected`-refresh effect in src/pages/admin/Users.tsx.
  // A caller that passes back the same `user` after `onSaved()` cannot tell a
  // succeeded row from a failed one, and every pending row looks unresolved
  // forever.
  const original = useMemo(() => buildDraft(user, workspaces), [user, workspaces]);

  // Reseed only when the modal opens or the subject changes — NOT on every
  // `user` reference change, or a background reload would silently wipe edits
  // that have not been saved yet.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(buildDraft(user, workspaces));
    setSelected(new Set());
    setBulkRole("");
    setSearch("");
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? workspaces.filter((w) => w.name.toLowerCase().includes(q)) : workspaces;
  }, [workspaces, search]);

  // `id in original` keeps a workspace that disappeared while the modal was open
  // (deleted by someone else) from counting as pending: `draft` holds the dead
  // key but no row renders for it, so it would read as a change the master can
  // neither see nor clear — and Save would write to a workspace that is gone.
  const pending = useMemo(
    () => Object.keys(draft).filter((id) => id in original && draft[id] !== original[id]),
    [draft, original]
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    visible.length > 0 && visible.every((w) => selected.has(w.id));

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((w) => next.delete(w.id));
      else visible.forEach((w) => next.add(w.id));
      return next;
    });

  const applyBulk = () =>
    setDraft((prev) => {
      const next = { ...prev };
      selected.forEach((id) => {
        next[id] = bulkRole === "" ? null : (bulkRole as WorkspaceRole);
      });
      return next;
    });

  const handleClose = useCallback(() => {
    if (
      pending.length > 0 &&
      !window.confirm("Discard unsaved access changes?")
    ) {
      return;
    }
    onClose();
  }, [pending.length, onClose]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setErrors({});

    // Best-effort: every change is attempted even if an earlier one fails, so a
    // single bad row cannot silently drop the rest. Neither service throws —
    // both return ServiceResult — so Promise.all is safe here.
    const results = await Promise.all(
      pending.map(async (workspaceId) => {
        const next = draft[workspaceId];
        const result =
          next === null
            ? await removeMember(user.id, workspaceId)
            : await setWorkspaceRole(user.id, workspaceId, next);
        return { workspaceId, result };
      })
    );

    const failed: Record<string, string> = {};
    results.forEach(({ workspaceId, result }) => {
      if (!result.success) {
        failed[workspaceId] = result.error ?? "The change did not go through";
      }
    });

    // Reload regardless of outcome, so the table and this modal report what the
    // server actually holds rather than what the draft assumed.
    await onSaved();
    setSaving(false);

    if (Object.keys(failed).length === 0) {
      onClose();
      return;
    }
    setErrors(failed);
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Manage access — ${user.email}`}
      description="Tick workspaces to change several at once. Nothing is saved until you press Save."
      size="full"
      footer={
        <div className="flex items-center justify-between">
          <span className="text-sm text-neutral-700">
            {pending.length === 0
              ? "No changes"
              : `${pending.length} change${pending.length === 1 ? "" : "s"} pending`}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={pending.length === 0 || saving}
              loading={saving}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <input
            type="search"
            aria-label="Search workspaces"
            placeholder="Search workspaces…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          />
          <label className="flex items-center gap-2 text-sm text-neutral-700 whitespace-nowrap">
            <input
              type="checkbox"
              aria-label="Select all visible workspaces"
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
            />
            Select all
          </label>
        </div>

        <div className="max-h-80 overflow-y-auto divide-y divide-neutral-100 border border-neutral-200 rounded-md">
          {visible.length === 0 ? (
            <p className="text-sm text-neutral-700 px-3 py-6 text-center">
              No workspaces match “{search}”.
            </p>
          ) : (
            visible.map((workspace) => {
              const role = draft[workspace.id] ?? null;
              const error = errors[workspace.id];
              return (
                <div key={workspace.id} className="flex items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${workspace.name}`}
                    checked={selected.has(workspace.id)}
                    onChange={() => toggle(workspace.id)}
                  />
                  <span
                    aria-hidden="true"
                    className={
                      role
                        ? "w-2 h-2 rounded-full bg-green-600"
                        : "w-2 h-2 rounded-full border border-neutral-300"
                    }
                  />
                  {/* Phones stack the role under the name: side by side the
                      name got ~110px and the fixture names ran to 5 lines. */}
                  <div className="flex-1 min-w-0 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Wraps on phones instead of truncating: the fixture
                          names all share a long prefix, so a cut name there
                          reads "[MMM FIXTU…" on every row. */}
                      <div className="text-ink-900 break-words sm:truncate" title={workspace.name}>
                        {workspace.name}
                      </div>
                      {error && <div className="text-xs text-red-700">{error}</div>}
                    </div>
                    <select
                      aria-label={`Role for ${workspace.name}`}
                      value={role ?? ""}
                      disabled={saving}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          [workspace.id]:
                            e.target.value === "" ? null : (e.target.value as WorkspaceRole),
                        }))
                      }
                      className="self-start sm:self-auto text-sm border border-neutral-300 rounded-md px-2 py-1"
                    >
                      <option value="">No access</option>
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* flex-wrap + nowrap pieces: on a narrow screen the controls move to
            a second line whole, instead of each label breaking mid-phrase. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
          <span className="text-sm text-neutral-700 whitespace-nowrap">{selected.size} selected</span>
          <span className="text-sm text-neutral-700 whitespace-nowrap">→ set to</span>
          <select
            aria-label="Role for selected workspaces"
            value={bulkRole}
            disabled={saving || selected.size === 0}
            onChange={(e) => setBulkRole(e.target.value)}
            className="text-sm border border-neutral-300 rounded-md px-2 py-1"
          >
            <option value="">No access</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={applyBulk}
            disabled={saving || selected.size === 0}
            className="whitespace-nowrap"
          >
            Apply to {selected.size}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageAccessModal;
